import axios from 'axios'

import { baseApi } from 'src/utils'
import { HOST_API, REFRESH_TOKEN_KEY } from 'src/constants'
import { decrypt } from 'src/utils/storage'
import { API_ENDPOINTS } from './api-endpoints'

import {
  CloneUserBody,
  CreateUserBody,
  GetUsersParams,
  UpdateUserBody,
  GetCompanyParams,
  PaginationParams,
  CreateUserCompany,
  GetUserStatusParams,
  UserSalesOfficeBody,
  GetPermissionsParams,
  CreateUserProfileBody,
  UpdateUserPermissionsBody,
  CreateUserBusinessPartners,
  DeleteUserSalesOfficesBody,
} from './types'

export const getSession = (payload: { email: string; password: string }) => {
  const combined = `${payload.email}:${payload.password}`
  const base64Encoded = btoa(combined)

  const headers = {
    Authorization: `Basic ${base64Encoded}`,
  }

  return baseApi
    .post(API_ENDPOINTS.auth.getSession, {}, { headers })
    .then((response) => response)
}

export const changePassword = async (payload: {
  email: string
  password: string
}) => {
  const response = await baseApi.post(API_ENDPOINTS.user.changePassword, {
    userName: payload.email,
    password: payload.password,
  })
  return response
}

export const sendVerificationToken = (payload: { Username: string }) =>
  baseApi
    .post(API_ENDPOINTS.user.sendVerificationCode, payload)
    .then((res) => !!(res.status === 200))

export const resetPassword = (payload: {
  Username: string
  NewPassword: string
  ConfirmationCode: string
}) => baseApi.post(API_ENDPOINTS.user.resetPassword, payload).then((res) => res)

export const getUsers = (params?: GetUsersParams) =>
  baseApi.get(API_ENDPOINTS.user.base, { params }).then((res) => res.data)

export const refreshToken = () => {
  const headers = {
    'X-Refresh-Token': decrypt(localStorage.getItem(REFRESH_TOKEN_KEY) || ''),
  }

  return axios
    .post(`${HOST_API}${API_ENDPOINTS.auth.refresh}`, {}, { headers })
    .then((res) => {
      return res.data.idToken
    })
}

export const getUserById = (userId: string) =>
  baseApi.get(API_ENDPOINTS.user.byId(userId)).then((res) => res.data)

export const updateUser = (userId: string, payload: UpdateUserBody) =>
  baseApi.put(API_ENDPOINTS.user.byId(userId), payload).then((res) => res)

export const createUser = (payload?: CreateUserBody) =>
  baseApi.post(API_ENDPOINTS.user.base, payload).then((res) => res)

export const cloneUser = (payload: CloneUserBody) =>
  baseApi.post(API_ENDPOINTS.user.clone, payload).then((res) => res.data)

export const getUserStatuses = (params?: GetUserStatusParams) =>
  baseApi.get(API_ENDPOINTS.user.userStatus, { params }).then((res) => res.data)

export const getUserProfiles = (userId: string) =>
  baseApi.get(API_ENDPOINTS.user.userProfiles(userId)).then((res) => res.data)

export const getUserBusinessPartners = (profileId: string) =>
  baseApi
    .get(API_ENDPOINTS.userProfile.businessPartners(profileId))
    .then((res) => res.data)

export const deleteUserBusinessPartner = (profileId: string, bpId: string) =>
  baseApi
    .delete(API_ENDPOINTS.userProfile.businessPartners(profileId), {
      data: { ids: [bpId] },
    })
    .then((res) => res.data)

export const createUserCompany = (
  profileId: string,
  payload: CreateUserCompany
) =>
  baseApi
    .post(API_ENDPOINTS.userProfile.userCompany(profileId), payload)
    .then((res) => res)

export const deleteUserCompany = (profileId: string, companyId: string) =>
  baseApi
    .delete(API_ENDPOINTS.userProfile.companyById(profileId, companyId))
    .then((res) => res.data)

export const updateMainUserCompany = (profileId: string, companyId: string) =>
  baseApi
    .put(API_ENDPOINTS.userProfile.companyById(profileId, companyId))
    .then((res) => res.data)

export const createUserBusinessPartner = (
  bpId: string,
  payload: CreateUserBusinessPartners[]
) =>
  baseApi
    .post(API_ENDPOINTS.userProfile.businessPartners(bpId), payload)
    .then((res) => res.data)

export const getUserCompanies = (
  profileId: string,
  params?: GetCompanyParams
) =>
  baseApi
    .get(API_ENDPOINTS.userProfile.userCompany(profileId), { params })
    .then((res) => res.data)

export const createUserProfile = (payload: CreateUserProfileBody) =>
  baseApi.post(API_ENDPOINTS.userProfile.base, payload).then((res) => res)

export const deleteUserProfile = (profileId: string) =>
  baseApi
    .delete(API_ENDPOINTS.userProfile.byId(profileId))
    .then((res) => res.data)

export const getUserPermissions = (
  profileId: string,
  params: PaginationParams
) =>
  baseApi
    .get(API_ENDPOINTS.userProfile.permissions(profileId), { params })
    .then((res) => res.data)

export const updateUserPermissions = (
  profileId: string,
  payload: UpdateUserPermissionsBody[]
) =>
  baseApi
    .post(API_ENDPOINTS.userProfile.permissions(profileId), payload)
    .then((res) => res.data)

export const getUserWorkflowTypes = (profileId: string) =>
  baseApi
    .get(API_ENDPOINTS.userProfile.workflowTypes(profileId))
    .then((res) => res.data)

export const createUserWorkflowTypes = (
  profileId: string,
  workflowTypes: string[]
) =>
  baseApi
    .post(API_ENDPOINTS.userProfile.workflowTypes(profileId), workflowTypes)
    .then((res) => res.data)

export const getSystemPermissions = (params?: GetPermissionsParams) =>
  baseApi.get(API_ENDPOINTS.user.permission, { params }).then((res) => res.data)

export const getUserSalesOffices = (profileId: string) =>
  baseApi
    .get(API_ENDPOINTS.userProfile.salesOffices(profileId))
    .then((res) => res.data)

export const createUserSalesOffice = (
  profileId: string,
  payload: UserSalesOfficeBody[]
) =>
  baseApi
    .post(API_ENDPOINTS.userProfile.salesOffices(profileId), payload)
    .then((res) => res.data)

export const deleteUserSalesOffice = (
  profileId: string,
  payload: DeleteUserSalesOfficesBody
) =>
  baseApi
    .delete(API_ENDPOINTS.userProfile.salesOffices(profileId), {
      data: payload,
    })
    .then((res) => res.data)

export const forceResetPassword = async (payload: {
  username: string
}) => {
  const response = await baseApi.patch(
    API_ENDPOINTS.user.forceResetPassword,
    payload
  )
  return response.data
}
